import {
  Button,
  IFormValues,
  Icon,
  Modal,
  NotificationService,
  PageContent,
  PageHeader,
  useSearchParamsFilters,
} from "@xala/common-ui";
import { BreadcrumbProps } from "antd/lib/breadcrumb";
import { generateBreadcrumb } from "../../../../helpers";
import { ROUTES } from "../../constants/routes";
import React from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";
import {
  GuidHelper,
  INotificationModel,
  NotificationsService,
  RecordStatus,
  TimeHelper,
  useDataLoader,
  useServiceCaller,
} from "@xala/common-services";
import { FormInstance } from "antd/lib/form";
import { FullFormTab } from "./tabs/FullFormTab";


const userNotificationService = new NotificationsService().promisify();
const notificationService = NotificationService.getInstance();

export const NotificationDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { current } = useSearchParamsFilters({ type: "string" });

  const { data: notification, refresh: notificationRefresh } = useDataLoader({
    loader: () => userNotificationService.get(+id),
    deps: [id],
  });

  /** While data loads, `notification` is undefined; `!notification?.Id` would wrongly be true (create mode). */
  const isEditRouteWithNumericId =
    id != null &&
    id !== "" &&
    !Number.isNaN(Number(id)) &&
    Number(id) > 0;

  const isCreateMode = notification
    ? !notification.Id
    : !isEditRouteWithNumericId;

  const [createNotification] = useServiceCaller(
    async (
      data: INotificationModel,
    ) => {
      const provider = current?.type === "push" ? "FIREBASE" : current?.type?.toUpperCase() || "";
      const result = await userNotificationService.insert({
        ...data,
        Provider: provider,
        Guid: GuidHelper.newGuid(),
        RecordStatus: RecordStatus.Inserted,
      });
      if (result.ok) {
        notificationService.success({
          message: t("NOTIFICATION_CREATE_SUCCESSFULLY"),
        });

        history.push(`${ROUTES.NOTIFICATION_DETAILS}/${result.data.Id}`);

        return result.ok;
      } else {
        notificationService.error({
          message: t("NOTIFICATION_CREATE_FAILURE"),
        });
      }
    },
    []
  );

  const [updateNotification] = useServiceCaller(
    async (
      data: INotificationModel,
      form: FormInstance<IFormValues>,
      isSended?: boolean
    ) => {
      const result = await userNotificationService.update({
        ...data,
        RecordStatus: RecordStatus.Updated,
        Guid: notification?.Guid,
      });
      if (result.ok) {
        notificationService.success({
          message: t("NOTIFICATION_UPDATE_SUCCESSFULLY"),
        });
      } else {
        notificationService.error({
          message: t("NOTIFICATION_UPDATE_FAILURE"),
        });
      }

      if (result.ok && isSended) {
        history.push(
          data.Provider
            ? `${ROUTES.NOTIFICATION_LIST}?Providers=${data.Provider}`
            : ROUTES.NOTIFICATION_LIST
        );
      }
      if (result.ok && !isSended) {
        await notificationRefresh();
      }
      form.resetFields();
      return result.ok;
    },
    [history, notification, notificationRefresh]
  );

  const [deleteNotification] = useServiceCaller(
    async (data: INotificationModel) => {
      const result = await userNotificationService.delete(data);
      if (result.ok) {
        notificationService.success({
          message: t("NOTIFICATION_DELETE_SUCCESSFULLY"),
        });
        history.push(ROUTES.NOTIFICATION_LIST);
      } else {
        notificationService.error({
          message: t("NOTIFICATION_DELETE_FAILURE"),
        });
      }
      return result.ok;
    },
    []
  );

  const getBreadcrumbProps = (): BreadcrumbProps => {
    return generateBreadcrumb([
      {
        path:
          current?.type
            ? `${ROUTES.NOTIFICATION_LIST}?Providers=${current.type}`
            : notification?.Provider
            ? `${ROUTES.NOTIFICATION_LIST}?Providers=${notification.Provider}`
            : `${ROUTES.NOTIFICATION_LIST}`,
        breadcrumbName:
          current?.type 
          ? t(`NOTIFICATION_LIST_${current.type.toUpperCase()}`) 
          : notification?.Provider 
          ? t(`NOTIFICATION_LIST_${notification.Provider.toUpperCase()}`) 
          : t("NOTIFICATION_LIST"),
      },
      {
        path: `${ROUTES.NOTIFICATION_DETAILS}/${id}`,
        breadcrumbName: notification?.Name ?? t("NOTIFICATIONS"),
      },
    ]);
  };

  const onSubmit = (
    data: INotificationModel,
    form: FormInstance<IFormValues>,
    isSended?: boolean,
    recipients?: any[]
  ) => {
    if (notification?.Id) {
      return updateNotification(data, form, isSended);
    }

    return createNotification(data);
  };

  const getExtraButtons = () => {
    if (notification?.Id) {
      return (
        <Button
          onClick={() => onDeleteClick()}
          title={t("NOTIFICATION_DELETE")}
          shape="circle"
          danger
          disabled={
            TimeHelper.isAfter(notification?.ScheduledOn || "") ||
            notification?.Status === "SCHEDULED" ||
            notification?.Status === "SENT" ||
            notification?.Status === "IN_PROGRESS"
          }
          icon={<Icon type="delete" />}
        />
      );
    }
  };

  const onDeleteClick = () => {
    if (!notification) {
      return;
    }

    Modal.confirm({
      title: t("Delete"),
      content: `Are you sure you want to delete "${notification?.Name}" notification?`,
      okText: "Delete",
      cancelText: "Cancel",
      onOk: () => deleteNotification(notification),
    });
  };

  return (
    <PageContent>
      {(notification || current?.type) ?
        <PageHeader
          breadcrumb={getBreadcrumbProps()}
          title={notification?.Provider ? t(`NOTIFICATION_DETAILS_${notification.Provider?.toUpperCase()}`) : t(`NOTIFICATION_CREATE_${current?.type?.toUpperCase()}`)}
          extra={getExtraButtons}
        /> :
        <>
          <br /><br /><br />
        </>
      }
      <FullFormTab
        key={notification?.Id?.toString() || "0"}
        notification={notification}
        onSubmit={onSubmit}
        isCreateMode={isCreateMode}
      />
    </PageContent>
  );
};